import { defineStore } from 'pinia'
import { ref } from 'vue'
import apiInstance from '@/utils/api'

export interface Role {
  id: number
  name: string
  display_name?: string
  description?: string
  permissions?: string[]
  permission_count?: number
  created_at?: string
  updated_at?: string
}

export interface RolesResponse {
  data: Role[]
  meta?: {
    total: number
    per_page: number
    current_page: number
    last_page: number
  }
}

export const useRolesStore = defineStore('roles', () => {
  const roles = ref<Role[]>([])
  const isLoading = ref(false)
  const error = ref('')
  const pagination = ref({
    total: 0,
    perPage: 10,
    currentPage: 1,
    lastPage: 1,
  })

  const fetchRoles = async () => {
    isLoading.value = true
    error.value = ''

    try {
      const response = await apiInstance.get('/admin/all-roles-and-permissions')
      const data = response.data

      if (response.status !== 200 || (data && data.error === true)) {
        error.value = data?.message || 'Failed to fetch roles'
        isLoading.value = false
        return
      }

      let rolesList: Role[] = []

      if (data?.data?.roles && Array.isArray(data.data.roles)) {
        rolesList = data.data.roles.map((role: any) => ({
          id: role.id,
          name: role.name,
          display_name: role.display_name || role.name,
          description: role.description || '',
          permissions: role.permissions || [],
          permission_count: Array.isArray(role.permissions) ? role.permissions.length : 0,
          created_at: role.created_at,
          updated_at: role.updated_at,
        }))
      }

      roles.value = rolesList

      pagination.value = {
        total: rolesList.length,
        perPage: rolesList.length,
        currentPage: 1,
        lastPage: 1,
      }
    } catch (err: any) {
      console.error('Error fetching roles:', err)
      error.value = err.response?.data?.message || 'Network error while fetching roles'
    } finally {
      isLoading.value = false
    }
  }

  const goToPage = () => {
    return fetchRoles()
  }

  const getRoleById = (roleId: number): Role | undefined => {
    return roles.value.find((r) => r.id === roleId)
  }

  const getRoleWithPermissions = async (roleId: number): Promise<{ role: Role; permissions: string[] }> => {
    try {
      // For now, we'll assume permissions need to be fetched from a separate endpoint
      // or we fetch them on-demand when editing a role
      const response = await apiInstance.get(`/admin/get-role-permissions/${roleId}`)
      const data = response.data

      if (response.status !== 200 || (data && data.error === true)) {
        throw new Error(data?.message || 'Failed to fetch role details')
      }

      const role: Role = {
        id: data.data.id,
        name: data.data.name,
        display_name: data.data.display_name || data.data.name,
        description: data.data.description || '',
        created_at: data.data.created_at,
        updated_at: data.data.updated_at,
      }

      const permissions: string[] = Array.isArray(data.data.permissions)
        ? data.data.permissions.map((p: any) => (typeof p === 'string' ? p : p.name))
        : []

      return { role, permissions }
    } catch (err: any) {
      console.error('Error fetching role with permissions:', err)
      throw new Error(err.response?.data?.message || err.message || 'Network error')
    }
  }

  const saveRolePermissions = async (
    roleId: number,
    name: string,
    permissions: { group: string; permissions: string[] }[]
  ) => {
    try {
      const response = await apiInstance.put(`/admin/sync-role-permissions/${roleId}`, {
        name,
        permissions,
      })
      const data = response.data

      if (response.status < 200 || response.status >= 300 || (data && data.error === true)) {
        console.error('API error:', data?.message)
        throw new Error(data?.message || 'Failed to save role permissions')
      }

      return {
        success: true,
        message: (data && data.message) || 'Role permissions saved successfully',
      }
    } catch (err: any) {
      console.error('Error saving role permissions:', err)
      throw new Error(err.response?.data?.message || err.message || 'Network error')
    }
  }

  const deleteRole = async (roleId: number) => {
    try {
      const response = await apiInstance.delete(`/admin/delete-role/${roleId}`)
      const data = response.data

      if (response.status < 200 || response.status >= 300 || (data && data.error === true)) {
        console.error('API error:', data?.message)
        throw new Error(data?.message || 'Failed to delete role')
      }

      return {
        success: true,
        message: (data && data.message) || 'Role deleted successfully',
      }
    } catch (err: any) {
      console.error('Error deleting role:', err)
      throw new Error(err.response?.data?.message || err.message || 'Network error')
    }
  }

  const reset = () => {
    roles.value = []
    isLoading.value = false
    error.value = ''
    pagination.value = {
      total: 0,
      perPage: 10,
      currentPage: 1,
      lastPage: 1,
    }
  }

  return {
    roles,
    isLoading,
    error,
    pagination,
    fetchRoles,
    goToPage,
    getRoleById,
    getRoleWithPermissions,
    saveRolePermissions,
    deleteRole,
    reset,
  }
})
